<template>
  <base-field
    :name="innerId"
    :label="label"
    :class="divClass"
    :optional="optional"
    :id="innerId + 'basefield'"
    style="position: relative"
  >
    <div
      @focusout="onFocusOut"
      style="position: relative"
      @click="showPicker = true"
    >
      <InputText
        :value="model?.toFormat('MM/dd/yyyy hh:mm a') || ''"
        readonly
        v-bind="$attrs"
      />

      <div
        ref="pickerContainer"
        style="position: absolute; top: 100%; z-index: 1000; left: 0"
        v-if="showPicker"
      >
        <DatePicker
          inline
          appendTo="self"
          v-bind="$attrs"
          :id="innerId"
          v-model="jsDate"
          :class="{ 'p-invalid is-invalid': showError }"
          :pt="{
            panel: {
              onmouseleave: onMouseLeave,
              onmouseenter: onMouseEnter,
            },
          }"
          :min-date="innerMinDate"
          :max-date="innerMaxDate"
          :show-other-months="true"
          :select-other-months="true"
          hourFormat="12"
          year-navigator
          show-icon
          autocomplete="off"
        />
      </div>
    </div>
    <HelpText
      :help-text="helpText"
      :error-message="errorMessage"
      :touched="touched"
    >
      <slot name="helpText" />
    </HelpText>
  </base-field>
</template>

<script lang="ts">
export default {
  name: "BaseDateTimePicker",
  inheritAttrs: false,
};
</script>

<script setup lang="ts">
import { DateTime } from "luxon";
import { computed, nextTick, ref, toRefs, useTemplateRef, watch } from "vue";

import { computedModel } from "@/utilities";
import { useBreakpoints } from "@/utilities/breakpoints";
import { useField, Validators } from "@/utilities/validations";
import DatePicker from "primevue/datepicker";
import InputText from "primevue/inputtext";

type ModelValue = DateTime | null;

interface Props {
  name: string;
  id?: string;
  modelValue?: ModelValue;
  minDate?: DateTime | null;
  maxDate?: DateTime | null;

  // Field Props
  label?: string;
  optional?: boolean;
  divClass?: string;

  // Validation Props
  rules?: Validators<ModelValue>;
  formName?: string;
  helpText?: string;
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: null,
  id: "",
  minDate: null,
  maxDate: null,
  rules: () => [],
});

const emit = defineEmits<{
  (e: "update:modelValue", value: ModelValue): void;
}>();

const pickerContainer = useTemplateRef("pickerContainer");

const innerId = computed(() => props.id || props.name);

const { rules, modelValue } = toRefs(props);
const model = computedModel(modelValue, emit);

const showPicker = ref(false);
const overPicker = ref(false);
const onMouseEnter = () => {
  overPicker.value = true;
};
const onMouseLeave = () => {
  overPicker.value = false;
};
const onFocusOut = async () => {
  await nextTick();
  if (!overPicker.value) {
    showPicker.value = false;
  }
};

const { touched, validate, errorMessage, showError } = useField(
  props.name,
  model,
  rules,
  {
    formName: props.formName,
  },
);

const jsDate = computed<Date | undefined>({
  get: () => model.value?.toJSDate(),
  set: (val) => (model.value = val ? DateTime.fromJSDate(val) : null),
});

watch(model, () => {
  nextTick(() => validate());
});

const innerMaxDate = computed(() => props.maxDate?.toJSDate());

const innerMinDate = computed(() => props.minDate?.toJSDate());

const { smaller } = useBreakpoints();
const isMobile = smaller("md");
</script>

<style scoped lang="scss">
input.form-control[readonly] {
  background-color: #ffffff;
}
</style>
